import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/layout/Header';
import ErrorBanner from '../../components/ui/ErrorBanner';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import apiClient from '../../api/apiClient';
import { formatDateTime } from '../../utils/formatters';
import { useAuth } from '../../context/AuthContext';

export default function AccountsPage() {
  const { refetchUser } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [renaming, setRenaming] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/accounts');
      setAccounts(res.data.accounts || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load accounts');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true); setFormError('');
    try {
      await apiClient.post('/accounts', { name });
      setName(''); setShowForm(false);
      await load();
    } catch (e) {
      setFormError(e.response?.data?.error || 'Failed to create account');
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this account? All associated data will be removed.')) return;
    try {
      await apiClient.delete(`/accounts/${id}`);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to delete account');
    }
  };

  const startEdit = (acct) => { setEditingId(acct.id); setEditName(acct.name); };
  const cancelEdit = () => { setEditingId(null); setEditName(''); };
  const handleRename = async (id) => {
    if (!editName.trim()) return;
    setRenaming(true);
    try {
      await apiClient.put(`/accounts/${id}`, { name: editName.trim() });
      setEditingId(null); setEditName('');
      await load();
      // The sidebar's account dropdown (myAccounts, in AuthContext) is only fetched once
      // on login/app-load and was never refreshed after a rename here - it kept showing
      // the old name until a full reload. Refetch it now so the rename shows up immediately.
      if (refetchUser) await refetchUser();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to rename account');
    } finally { setRenaming(false); }
  };

  return (
    <div>
      <Header title="Accounts" />
      <div style={styles.content}>
        <ErrorBanner message={error} />

        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>All Accounts ({accounts.length})</h3>
            <button onClick={() => setShowForm(v => !v)} style={styles.addBtn}>
              {showForm ? 'Cancel' : '+ New Account'}
            </button>
          </div>

          {showForm && (
            <form onSubmit={handleCreate} style={styles.inlineForm}>
              {formError && <div style={styles.formError}>{formError}</div>}
              <input value={name} onChange={e => setName(e.target.value)} required placeholder="Account name" style={styles.input} />
              <button type="submit" disabled={submitting} style={styles.submitBtn}>
                {submitting ? 'Creating…' : 'Create'}
              </button>
            </form>
          )}

          {loading ? <LoadingSpinner /> : (
            <table style={styles.table}>
              <thead>
                <tr>
                  {['ID','Name','Created','Actions'].map(h => <th key={h} style={styles.th}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {accounts.length === 0 ? (
                  <tr><td colSpan={4} style={styles.empty}>No accounts</td></tr>
                ) : accounts.map((acct, i) => (
                  <tr key={acct.id} style={i % 2 === 0 ? {} : { background: '#f8fafc' }}>
                    <td style={styles.td}>{acct.id}</td>
                    <td style={{ ...styles.td, fontWeight: '600' }}>
                      {editingId === acct.id ? (
                        <input
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleRename(acct.id); if (e.key === 'Escape') cancelEdit(); }}
                          autoFocus
                          style={{ ...styles.input, minWidth: '160px', padding: '0.3rem 0.5rem' }}
                        />
                      ) : acct.name}
                    </td>
                    <td style={styles.td}>{formatDateTime(acct.created_at)}</td>
                    <td style={styles.td}>
                      {editingId === acct.id ? (
                        <>
                          <button onClick={() => handleRename(acct.id)} disabled={renaming} style={styles.deleteBtn}>{renaming ? 'Saving…' : 'Save'}</button>
                          <button onClick={cancelEdit} style={{ ...styles.deleteBtn, color: '#64748b', marginLeft: '0.5rem' }}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEdit(acct)} style={{ ...styles.deleteBtn, color: '#6366f1' }}>Rename</button>
                          <button onClick={() => handleDelete(acct.id)} style={{ ...styles.deleteBtn, marginLeft: '0.5rem' }}>Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  content: { padding: '1.5rem 1.75rem' },
  card: { background: '#fff', borderRadius: '0.75rem', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  cardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' },
  cardTitle: { margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#1e293b' },
  addBtn: { padding: '0.4rem 0.875rem', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.8rem', fontWeight: '600' },
  inlineForm: { display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' },
  formError: { width: '100%', color: '#dc2626', fontSize: '0.8rem' },
  input: { flex: 1, padding: '0.5rem 0.75rem', border: '1.5px solid #e2e8f0', borderRadius: '0.5rem', fontSize: '0.9rem', outline: 'none', minWidth: '200px' },
  submitBtn: { padding: '0.5rem 1rem', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' },
  th: { padding: '0.625rem 0.75rem', background: '#f8fafc', color: '#64748b', fontWeight: '600', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left', borderBottom: '1px solid #e2e8f0' },
  td: { padding: '0.625rem 0.75rem', borderBottom: '1px solid #f1f5f9', color: '#1e293b', verticalAlign: 'middle' },
  deleteBtn: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '0.8rem', fontWeight: '600' },
  empty: { padding: '2rem', textAlign: 'center', color: '#94a3b8' },
};
